import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { X, Search, FileCode2, HelpCircle } from 'lucide-react';
import { PointRequestItem } from '@/types';

interface PointsEarningHistoryModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const STATUS_TRANSLATIONS: Record<string, string> = {
    'submitted': 'Enviado',
    'under_review': 'Em Análise',
    'validated': 'Validado',
    'approved': 'Aprovado',
    'rejected': 'Rejeitado',
    'canceled': 'Cancelado',
    'credited': 'Creditado',
};

const STATUS_TIMELINES: Record<string, string> = {
    'submitted': 'Aguardando início da análise (até 5 dias úteis).',
    'under_review': 'Em processo de análise (até 5 dias úteis).',
    'validated': 'Aguardando período de garantia da loja (de 30 a 60 dias).',
    'approved': 'Aprovado! Os pontos serão creditados em breve (até 2 dias úteis).',
    'rejected': 'A solicitação foi recusada por não cumprir os requisitos.',
    'canceled': 'A solicitação foi cancelada por motivos internos.',
    'credited': 'Pontos já adicionados ao seu saldo.',
};

const PROVIDER_TRANSLATIONS: Record<string, string> = {
    'mercadolivre': 'Mercado Livre',
    'amazon': 'Amazon',
    'shopee': 'Shopee',
    'magalu': 'Magalu',
    'aliexpress': 'AliExpress'
};

const STATUS_COLORS: Record<string, string> = {
    'submitted': 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    'under_review': 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
    'validated': 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    'approved': 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    'rejected': 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    'canceled': 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    'credited': 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
};

export const PointsEarningHistoryModal = ({ isOpen, onClose }: PointsEarningHistoryModalProps) => {
    const [items, setItems] = useState<PointRequestItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(0);

    const observerTarget = useRef<HTMLDivElement>(null);
    const initialLoadRef = useRef(false);

    const ITEMS_PER_PAGE = 15;

    useEffect(() => {
        if (isOpen) {
            if (!initialLoadRef.current) {
                resetAndFetch();
                initialLoadRef.current = true;
            }
        } else {
            initialLoadRef.current = false;
        }
    }, [isOpen]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && hasMore && !loading) {
                    fetchPage(page + 1);
                }
            },
            { threshold: 0.1 }
        );

        if (observerTarget.current) {
            observer.observe(observerTarget.current);
        }

        return () => observer.disconnect();
    }, [hasMore, loading, page]);

    const resetAndFetch = () => {
        setItems([]);
        setPage(0);
        setHasMore(true);
        fetchPage(0);
    };

    const fetchPage = async (pageToFetch: number) => {
        setLoading(true);
        try {
            const from = pageToFetch * ITEMS_PER_PAGE;
            const to = from + ITEMS_PER_PAGE - 1;

            const { data, error } = await supabase
                .from('points_earning_requests')
                .select('*')
                .order('created_at', { ascending: false })
                .range(from, to);

            if (error) throw error;

            const newItems = data || [];

            if (newItems.length < ITEMS_PER_PAGE) {
                setHasMore(false);
            }

            setItems(prev => pageToFetch === 0 ? newItems : [...prev, ...newItems]);
            setPage(pageToFetch);

        } catch (err) {
            console.error('Error fetching points requests:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('pt-BR', {
            timeZone: 'America/Sao_Paulo',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatCurrency = (value: number | null) => {
        if (value === null) return '-';
        return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-white/20 dark:border-gray-800 overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[85vh]">

                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/50">
                    <div className="flex items-center gap-2">
                        <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-full text-emerald-600 dark:text-emerald-400">
                            <FileCode2 className="w-5 h-5" />
                        </div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Minhas Solicitações</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-0 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-800">
                    {items.length === 0 && !loading ? (
                        <div className="flex flex-col items-center justify-center p-12 text-center h-full">
                            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                                <Search className="w-8 h-8 text-gray-400" />
                            </div>
                            <p className="text-gray-500 font-medium">Você ainda não fez nenhuma solicitação de pontos.</p>
                            <p className="text-sm text-gray-400 mt-1">
                                Suas solicitações enviadas aparecerão aqui.
                            </p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100 dark:divide-gray-800">
                            {items.map((item) => {
                                const providerLabel = PROVIDER_TRANSLATIONS[item.provider] || item.provider;
                                const statusLabel = STATUS_TRANSLATIONS[item.status] || item.status;
                                const statusColor = STATUS_COLORS[item.status] || 'bg-gray-100 text-gray-600';

                                return (
                                    <div key={item.id} className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors flex flex-col gap-2 group">
                                        <div className="flex items-center justify-between">
                                            <div className="flex flex-col gap-0.5">
                                                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                                    {providerLabel}
                                                </p>
                                                <p className="text-xs text-gray-400 font-mono">
                                                    {formatDate(item.created_at)}
                                                </p>
                                            </div>
                                            <div className="flex flex-col items-end gap-1">
                                                <div className="flex items-center gap-1.5">
                                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${statusColor}`}>
                                                        {statusLabel}
                                                    </span>
                                                    <div className="group/tooltip relative flex items-center">
                                                        <HelpCircle className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-help" />
                                                        <div className="absolute right-0 top-full mt-1 w-48 p-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-xs font-medium rounded-xl shadow-xl opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all z-10 pointer-events-none text-right">
                                                            {STATUS_TIMELINES[item.status] || 'Status da solicitação.'}
                                                            <div className="absolute right-1 -top-1 w-2 h-2 bg-gray-900 dark:bg-white transform rotate-45"></div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                                    Valor: {formatCurrency(item.amount)}
                                                </p>
                                            </div>
                                        </div>

                                        {(item.points_approved && item.status === 'credited') && (
                                            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg px-3 py-2 flex items-center justify-between">
                                                <span className="text-xs text-gray-500 dark:text-gray-400">Pontos Creditados:</span>
                                                <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                                                    +{item.points_approved || 0}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {hasMore && (
                                <div ref={observerTarget} className="py-6 text-center text-xs text-gray-400 flex items-center justify-center gap-2">
                                    {loading && <div className="w-3 h-3 rounded-full border-2 border-gray-400 border-t-transparent animate-spin"></div>}
                                    {loading ? 'Carregando mais...' : ''}
                                </div>
                            )}

                            {!hasMore && items.length > 0 && (
                                <div className="py-6 text-center text-xs text-gray-400 bg-gray-50/30 dark:bg-gray-800/20">
                                    Fim da lista
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="p-3 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50 text-center text-[10px] text-gray-400">
                    Mostrando {items.length} solicitações no total
                </div>
            </div>
        </div>
    );
};
